import * as THREE from 'three';
import type { Item, Vec2 } from '../../core/hp/types';
import type { SurfaceProbe, SurfaceLayer } from '../../core/geometry/stacking';
import { cloneFullModel, fullGeometryOf } from './modeVisibility';

/**
 * The height of a loaded model's surface under a plan point, for landing what is dragged over
 * it and carrying what stands on it (`src/core/geometry/landing.ts`, `stacking.ts`). Unity reads
 * this off mesh colliders; here a ray is cast straight down through a private copy of the model —
 * the one `cloneFullModel` makes, whole even while a wall mode cuts the drawn one — posed from the
 * item *data* it is asked about, never from the drawn node: during a drag the drawn node already
 * stands at the preview position while the question is about the piece where the gesture began.
 * The first hit is the top surface under that point. The copy is remade only when the model's
 * geometry changes, as it does when hydration replaces a placeholder.
 */

interface ProbeCopy { stamp: string; copy: THREE.Object3D; facade?: boolean; facadeScale?: string }
const copies = new WeakMap<THREE.Object3D, ProbeCopy>();
const caster = new THREE.Raycaster();
/** Well above any room: the ray starts here and looks down. */
const SKY = 100;

function stampOf(node: THREE.Object3D): string {
  const parts: string[] = [];
  node.traverse(child => {
    if (child !== node) parts.push(child.matrix.elements.join(','));
    if ((child as THREE.Mesh).isMesh) parts.push(fullGeometryOf(child as THREE.Mesh).uuid);
  });
  return parts.join('|');
}

/**
 * The highest surface of `node`'s model, posed as `item` says (the viewer's own placement rule:
 * yaw, elevation, a placeholder centred on its height), under any of `points` — as a height above
 * the floor, or null when none of the points is over its meshes.
 */
function posedCopy(node: THREE.Object3D, item: Item): ProbeCopy {
  const stamp = stampOf(node);
  let probe = copies.get(node);
  if (!probe || probe.stamp !== stamp) { probe = { stamp, copy: cloneFullModel(node) }; copies.set(node, probe); }
  const copy = probe.copy;
  copy.scale.copy(node.scale);
  copy.position.set(item.position.x, item.elevation + (node.userData.real ? 0 : item.size.h / 2), item.position.z);
  copy.rotation.set(0, -item.rotation, 0);
  copy.updateMatrixWorld(true);
  return probe;
}

export function meshTopAt(node: THREE.Object3D, item: Item, points: readonly Vec2[]): number | null {
  const { copy } = posedCopy(node, item);
  let best: number | null = null;
  for (const point of points) {
    caster.ray.origin.set(point.x, SKY, point.z); caster.ray.direction.set(0, -1, 0);
    const first = caster.intersectObject(copy, true)[0];
    if (first && (best === null || first.point.y > best)) best = first.point.y;
  }
  return best;
}

const HEIGHT_EPS = 1e-4;
const distinct = (values: number[]) => values.sort((a, b) => a - b).filter((height, i, all) => !i || height - all[i - 1] > HEIGHT_EPS);
const normalMatrix = new THREE.Matrix3();
const normal = new THREE.Vector3();
function facesRay(hit: THREE.Intersection): boolean {
  if (!hit.face) return false;
  normal.copy(hit.face.normal).applyNormalMatrix(normalMatrix.getNormalMatrix(hit.object.matrixWorld));
  return normal.dot(caster.ray.direction) < -.05;
}

/** Native HasFacadeBlockingShelves: both front-edge rays must see a facade and a back. */
function hasFacade(probe: ProbeCopy, item: Item): boolean {
  const scale = probe.copy.scale.toArray().join(',');
  if (probe.facade !== undefined && probe.facadeScale === scale) return probe.facade;
  const copy = probe.copy, bounds = new THREE.Box3().setFromObject(copy);
  const centre = bounds.getCenter(new THREE.Vector3());
  const front = new THREE.Vector3(Math.sin(-item.rotation), 0, Math.cos(item.rotation));
  const right = new THREE.Vector3(front.z, 0, -front.x);
  const halfDepth = item.size.d / 2, halfWidth = item.size.w / 2;
  const sideHits = [-1, 1].map(sign => {
    caster.ray.origin.copy(centre).addScaledVector(front, halfDepth + .05).addScaledVector(right, sign * halfWidth * .8);
    caster.ray.direction.copy(front).negate();
    return distinct(caster.intersectObject(copy, true).filter(hit => hit.distance <= 2 * halfDepth + .1 && facesRay(hit)).map(hit => hit.distance)).length;
  });
  probe.facadeScale = scale;
  return probe.facade = sideHits.every(count => count >= 2);
}

/** Upward and downward mesh hits preserve shelf tops, their thickness and the next underside. */
export function meshLayersAt(node: THREE.Object3D, item: Item, points: readonly Vec2[]): SurfaceLayer[] {
  const probe = posedCopy(node, item), copy = probe.copy;
  const columns = points.map(point => {
    caster.ray.origin.set(point.x, SKY, point.z); caster.ray.direction.set(0, -1, 0);
    const tops = distinct(caster.intersectObject(copy, true).filter(facesRay).map(hit => hit.point.y));
    caster.ray.origin.set(point.x, -SKY, point.z); caster.ray.direction.set(0, 1, 0);
    const bottoms = distinct(caster.intersectObject(copy, true).filter(facesRay).map(hit => hit.point.y));
    // A one-face shelf has no authored underside; its own plane is the clearance boundary.
    return { tops, bottoms: bottoms.length ? bottoms : tops };
  });
  const heights = distinct(columns.flatMap(column => column.tops));
  const facade = hasFacade(probe, item);
  return heights.map(top => {
    const matching = columns.filter(column => column.tops.some(height => Math.abs(height - top) <= HEIGHT_EPS));
    const lower = matching.flatMap(column => column.bottoms.filter(height => height < top - HEIGHT_EPS));
    const upper = columns.flatMap(column => column.bottoms.filter(height => height > top + HEIGHT_EPS));
    const ceiling = upper.length ? Math.min(...upper) : Infinity;
    return { bottom: lower.length ? Math.max(...lower) : top, top, ceiling,
      supported: matching.length === points.length, accessible: !Number.isFinite(ceiling) || !facade };
  });
}

/** A probe over the nodes `nodeFor` resolves; a placeholder box answers nothing, so the box top rules. */
export function meshSurfaceProbe(nodeFor: (name: string) => THREE.Object3D | undefined): SurfaceProbe {
  const probe: SurfaceProbe = (base: Item, points) => {
    const node = nodeFor(`item:${base.id}`);
    return node && (node.userData.real || node.userData.generated) ? meshTopAt(node, base, points) : null;
  };
  probe.layers = (base, points) => {
    const node = nodeFor(`item:${base.id}`);
    return node && (node.userData.real || node.userData.generated) ? meshLayersAt(node, base, points) : null;
  };
  return probe;
}
